import { 
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase.config';
import { LoginCredentials, RegisterData, User, AuthUser } from '../types/auth.types';

class AuthService {
  // 이메일/비밀번호 로그인
  async login(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      console.log('🔵 로그인 시도:', credentials.email);
      const result = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      console.log('🟢 로그인 성공:', result.user.uid);
      
      // Firestore에 사용자 문서가 없으면 생성
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        console.log('⚠️ Firestore 사용자 문서 없음, 생성 중...');
        const userData: User = {
          uid: result.user.uid,
          email: result.user.email!,
          name: result.user.displayName || result.user.email!.split('@')[0],
          displayName: result.user.displayName || result.user.email!.split('@')[0],
          role: 'user', // 기본값
          createdAt: new Date()
        };
        await setDoc(doc(db, 'users', result.user.uid), userData);
        console.log('✅ Firestore 사용자 문서 생성 완료');
      }
      
      const authUser = {
        uid: result.user.uid,
        email: result.user.email!,
        displayName: result.user.displayName || undefined
      };
      console.log('🟢 반환할 AuthUser:', authUser);
      
      return authUser;
    } catch (error: any) {
      console.error('🔴 로그인 에러:', error.message);
      throw error; // 원본 Firebase 에러 객체 전달
    }
  }

  // 로그아웃
  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  // 회원가입
  async register(data: RegisterData): Promise<AuthUser> {
    try {
      const result = await createUserWithEmailAndPassword(auth, data.email, data.password);
      
      // 프로필 업데이트
      await updateProfile(result.user, {
        displayName: data.displayName
      });

      // Firestore에 사용자 추가 정보 저장
      const userData: User = {
        uid: result.user.uid,
        email: data.email,
        name: data.displayName,
        displayName: data.displayName,
        role: 'user', // 기본값: user
        department: data.department,
        createdAt: new Date()
      };

      await setDoc(doc(db, 'users', result.user.uid), userData);

      return {
        uid: result.user.uid,
        email: result.user.email!,
        displayName: data.displayName
      };
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  // Firestore에서 사용자 상세 정보 가져오기
  async getUserData(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date()
        } as User;
      }
      return null;
    } catch (error: any) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  // 인증 상태 변화 리스너
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  // 현재 사용자 정보
  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }
}

export const authService = new AuthService();
